import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, X } from 'lucide-react';

import KanbanTab from './admin/KanbanTab';
import OrdersTab from './admin/OrdersTab';
import PartialPaidTab from './admin/PartialPaidTab';
import OrderPoolTab from './admin/OrderPoolTab';
import MeasurementsTab from './admin/MeasurementsTab';
import BranchToggle from './admin/BranchToggle';
import PhoneInput from '@/components/PhoneInput';
import { STATUS_CONFIG, DASHBOARD_STATUSES, getTabsForRole } from './admin/utils';

export default function AdminDashboard() {
  const { user } = useAuth();
  const tabs = getTabsForRole(user?.role);
  const [activeTab, setActiveTab] = useState(tabs[0]?.key || 'kanban');

  return (
    <div className="max-w-[1500px] mx-auto px-3 sm:px-4 py-6" data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold mb-1">Admin Panel</h1>
      <p className="text-sm text-muted-foreground mb-6">{user?.name} &middot; {user?.role?.replace(/_/g,' ')}</p>
      <div className="flex gap-2 overflow-x-auto pb-3 mb-6" data-testid="admin-tabs">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${activeTab === tab.key ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
            data-testid={`admin-tab-${tab.key}`}
          >
            <tab.icon className="w-3.5 h-3.5" /> {tab.label}
          </button>
        ))}
      </div>
      {activeTab === 'dashboard' && <DashboardTab role={user?.role} />}
      {activeTab === 'kanban' && <KanbanTab role={user?.role} />}
      {activeTab === 'orders' && <OrdersTab />}
      {activeTab === 'partial_paid' && <PartialPaidTab />}
      {activeTab === 'order_pool' && <OrderPoolTab />}
      {activeTab === 'employees' && <EmployeesTab role={user?.role} />}
      {activeTab === 'measurements' && <MeasurementsTab />}
      {activeTab === 'branches' && <BranchesTab />}
      {activeTab === 'callbacks' && <CallbacksTab />}
      {activeTab === 'consultations' && <ConsultationsTab />}
      {activeTab === 'pincodes' && <PincodesTab />}
      {activeTab === 'audit' && <AuditTab />}
    </div>
  );
}

// ── Dashboard (stat cards across all statuses) ──
function DashboardTab({ role }) {
  const [stats, setStats] = useState({});
  const [branches, setBranches] = useState([]);
  const [sel, setSel] = useState('');
  const isSA = role === 'SUPER_ADMIN';

  useEffect(() => { if (isSA) api.get('/admin/branches').then(r => setBranches(r.data.data||[])).catch(()=>{}); }, [isSA]);
  useEffect(() => {
    const p = sel ? `?branch_id=${sel}` : '';
    api.get(`/admin/dashboard/stats${p}`).then(r => setStats(r.data.data||{})).catch(()=>{});
  }, [sel]);

  const total = Object.values(stats).reduce((a,b) => a+b, 0);
  return (
    <div data-testid="dashboard-tab">
      {isSA && branches.length > 0 && (
        <BranchToggle branches={branches} value={sel} onChange={setSel} testIdPrefix="dashboard-branch" />
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total Orders" value={total} className="bg-primary/5 border-primary/20" />
        {DASHBOARD_STATUSES.map(s => {
          const cfg = STATUS_CONFIG[s];
          return <StatCard key={s} label={cfg?.label||s} value={stats[s]||0} className={cfg?.color} />;
        })}
      </div>
    </div>
  );
}

function StatCard({ label, value, className='' }) {
  return (
    <div className={`rounded-xl p-4 border ${className||'bg-card border-border'}`}>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs font-medium mt-1">{label}</p>
    </div>
  );
}

// ── Employees ──
function EmployeesTab({ role }) {
  const [employees, setEmployees] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [branches, setBranches] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [includeInactive, setIncludeInactive] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [detail, setDetail] = useState(null);
  const blankForm = {phone:'', name:'', email:'', role:'TAILOR', branch_id:'', temp_password:'', notify:false, identification_url:'', date_of_joining: new Date().toISOString().slice(0,10)};
  const [form, setForm] = useState(blankForm);
  const [uploading, setUploading] = useState(false);
  const limit = 15;
  const isSA = role === 'SUPER_ADMIN';
  const allowedRoles = isSA ? ['BRANCH_ADMIN','TAILOR','PICKUP_AGENT','SALES_AGENT'] : ['TAILOR','PICKUP_AGENT','SALES_AGENT'];
  const branchName = (id) => branches.find(b => b.id === id)?.name || '—';

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [debouncedSearch, branchFilter, includeInactive]);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    if (debouncedSearch) params.append('q', debouncedSearch);
    if (branchFilter) params.append('branch_id', branchFilter);
    params.append('include_inactive', includeInactive ? 'true' : 'false');
    api.get(`/admin/employees?${params.toString()}`).then(r => {
      const d = r.data.data || {};
      setEmployees(d.employees || []);
      setTotal(d.total || 0);
    }).catch(() => {});
  }, [page, debouncedSearch, branchFilter, includeInactive]);

  useEffect(() => { api.get('/admin/branches').then(r => setBranches(r.data.data||[])).catch(()=>{}); }, []);
  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/upload/document', fd, { headers: {'Content-Type': 'multipart/form-data'} });
      setForm(f => ({...f, identification_url: r.data.data.url}));
      toast.success('Identification uploaded');
    } catch(e) { toast.error(e.response?.data?.error||'Upload failed'); }
    finally { setUploading(false); }
  };

  const handleCreate = async () => {
    if (!form.phone || !form.name || !form.branch_id) { toast.error('Phone, name and branch required'); return; }
    if (!form.temp_password || form.temp_password.length < 6) { toast.error('Temp password must be at least 6 characters'); return; }
    try {
      await api.post('/admin/employees', form);
      toast.success(form.notify
        ? 'Employee added — temp password sent via WhatsApp/SMS. They will reset it on first login.'
        : 'Employee added — share the temp password with them. They will reset it on first login.');
      setForm(blankForm);
      setShowAdd(false);
      load();
    } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this employee? They will lose admin privileges immediately.')) return;
    try { await api.put(`/admin/employees/${id}`, {is_active:false}); toast.success('Deactivated'); setDetail(null); load(); }
    catch { toast.error('Failed'); }
  };
  const handleReactivate = async (id) => {
    try { await api.put(`/admin/employees/${id}/reactivate`, {}); toast.success('Employee reactivated — privileges restored'); setDetail(null); load(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="employees-tab">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Input
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          placeholder="Search by name or phone..."
          className="max-w-xs"
          data-testid="employee-search"
        />
        {isSA && branches.length > 0 && (
          <Select value={branchFilter || '__ALL__'} onValueChange={(v) => setBranchFilter(v === '__ALL__' ? '' : v)}>
            <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="employee-branch-filter">
              <SelectValue placeholder="All Branches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__ALL__">All Branches</SelectItem>
              {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox
            checked={includeInactive}
            onCheckedChange={(v) => setIncludeInactive(!!v)}
            data-testid="employee-include-inactive"
          />
          <span>Show deactivated</span>
        </label>
        <div className="ml-auto" />
        {!showAdd && (
          <Button variant="outline" onClick={() => setShowAdd(true)} className="rounded-full" data-testid="add-employee-toggle">
            <Plus className="w-4 h-4 mr-2" /> Add Employee
          </Button>
        )}
      </div>

      {showAdd && (
        <div className="border rounded-xl p-4 mb-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <PhoneInput value={form.phone} onChange={(v) => setForm({...form, phone:v})} placeholder="10-digit phone" data-testid="employee-phone" />
            <Input value={form.name} onChange={e => setForm({...form, name:e.target.value})} placeholder="Full Name" data-testid="employee-name" />
            <Select value={form.role} onValueChange={(v) => setForm({...form, role:v})}>
              <SelectTrigger className="h-9 text-sm" data-testid="employee-role">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                {allowedRoles.map(r => (
                  <SelectItem key={r} value={r} data-testid={`employee-role-${r}`}>{r.replace(/_/g,' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={form.branch_id || undefined} onValueChange={(v) => setForm({...form, branch_id:v})}>
              <SelectTrigger className="h-9 text-sm" data-testid="employee-branch">
                <SelectValue placeholder="Select Branch" />
              </SelectTrigger>
              <SelectContent>
                {branches.map(b => (
                  <SelectItem key={b.id} value={b.id} data-testid={`employee-branch-${b.id}`}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div>
              <label className="text-xs text-muted-foreground">Date of Joining</label>
              <Input type="date" value={form.date_of_joining} onChange={e => setForm({...form, date_of_joining:e.target.value})} data-testid="employee-doj" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Identification (Aadhar / PAN / Passport — image or PDF)</label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp,.pdf"
                  onChange={e => handleUpload(e.target.files?.[0])}
                  disabled={uploading}
                  data-testid="employee-identification-upload"
                />
                {form.identification_url && (
                  <a href={form.identification_url} target="_blank" rel="noreferrer" className="text-xs text-primary underline whitespace-nowrap" data-testid="employee-identification-preview">View uploaded</a>
                )}
              </div>
            </div>
            <div className="sm:col-span-2">
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  value={form.temp_password}
                  onChange={e => setForm({...form, temp_password:e.target.value})}
                  placeholder="Temporary password (min 6 chars)"
                  data-testid="employee-temp-password"
                />
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={() => {
                    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
                    let pw = '';
                    for (let i=0; i<10; i++) pw += chars[Math.floor(Math.random()*chars.length)];
                    setForm({...form, temp_password: pw});
                  }}
                  data-testid="employee-temp-password-generate"
                >Generate</Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                Share this with the employee privately. They will be required to set a new password on their first login.
              </p>
            </div>
            <label className="sm:col-span-2 flex items-center gap-2 cursor-pointer text-sm">
              <Checkbox checked={form.notify} onCheckedChange={(v) => setForm({...form, notify: !!v})} data-testid="employee-notify" />
              <span>Notify employee via WhatsApp / SMS with their temp password</span>
            </label>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreate} className="rounded-full" data-testid="employee-save">Save</Button>
            <Button variant="outline" onClick={() => { setShowAdd(false); setForm(blankForm); }} className="rounded-full">Cancel</Button>
          </div>
        </div>
      )}

      <div className="space-y-2" data-testid="employee-results">
        <div className="flex justify-end mb-1">
          <span className="text-xs text-muted-foreground" data-testid="employees-total">
            {total} {total === 1 ? 'result' : 'results'}
          </span>
        </div>
        {employees.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No employees match.</p>}
        {employees.map(emp => (
          <div
            key={emp.id}
            className="flex items-center justify-between border rounded-xl px-4 py-3 hover:bg-muted/30 cursor-pointer"
            onClick={() => setDetail(emp)}
            data-testid={`employee-row-${emp.id}`}
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium flex items-center gap-2">
                <span className="truncate">{emp.name}</span>
                <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${emp.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {emp.is_active ? 'Active' : 'Deactivated'}
                </span>
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {emp.phone} &middot; <span className="text-primary font-medium">{emp.role?.replace(/_/g,' ')}</span>
                {isSA && emp.branch_id && <span> &middot; {branchName(emp.branch_id)}</span>}
              </p>
            </div>
          </div>
        ))}
      </div>

      {total > 0 && (
        <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-border/50">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="text-xs h-7" data-testid="employees-prev-btn">Prev</Button>
          <span className="text-xs text-muted-foreground tabular-nums" data-testid="employees-pagination">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="text-xs h-7" data-testid="employees-next-btn">Next</Button>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDetail(null)} data-testid="employee-detail">
          <div className="bg-background rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Employee Details</h3>
              <span className={`text-xs uppercase font-semibold px-2 py-0.5 rounded ${detail.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {detail.is_active ? 'Active' : 'Deactivated'}
              </span>
            </div>
            <div className="space-y-2 text-sm">
              <p><span className="text-muted-foreground">Name:</span> <span className="font-medium">{detail.name}</span></p>
              <p><span className="text-muted-foreground">Phone:</span> {detail.phone}</p>
              {detail.email && <p><span className="text-muted-foreground">Email:</span> {detail.email}</p>}
              <p><span className="text-muted-foreground">Role:</span> {detail.role?.replace(/_/g,' ')}</p>
              {detail.branch_id && <p><span className="text-muted-foreground">Branch:</span> {branchName(detail.branch_id)}</p>}
              {detail.date_of_joining && !String(detail.date_of_joining).startsWith('0001') && (
                <p><span className="text-muted-foreground">Date of Joining:</span> {new Date(detail.date_of_joining).toLocaleDateString()}</p>
              )}
              {detail.identification_url && (
                <p>
                  <span className="text-muted-foreground">Identification:</span>{' '}
                  <a href={detail.identification_url} target="_blank" rel="noreferrer" className="text-primary underline" data-testid="employee-detail-identification">View document</a>
                </p>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 mt-6">
              {detail.role !== 'SUPER_ADMIN' && detail.is_active && (
                <Button variant="outline" className="text-destructive" onClick={() => handleDeactivate(detail.id)} data-testid="employee-detail-deactivate">Deactivate</Button>
              )}
              {!detail.is_active && (
                <Button onClick={() => handleReactivate(detail.id)} data-testid="employee-detail-reactivate">Reactivate</Button>
              )}
              <Button variant="ghost" onClick={() => setDetail(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Branches ──
function BranchesTab() {
  const [branches, setBranches] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({name:'', address:'', phone:''});

  const load = () => api.get('/admin/branches').then(r => setBranches(r.data.data||[])).catch(()=>{});
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    try { await api.post('/admin/branches', form); toast.success('Branch created'); setForm({name:'', address:'', phone:''}); setShowAdd(false); load(); }
    catch(e) { toast.error(e.response?.data?.error||'Failed'); }
  };

  return (
    <div data-testid="branches-tab">
      {!showAdd ? (
        <Button variant="outline" onClick={() => setShowAdd(true)} className="rounded-full mb-4">
          <Plus className="w-4 h-4 mr-2" /> Add Branch
        </Button>
      ) : (
        <div className="border rounded-xl p-4 mb-4 space-y-3">
          <Input value={form.name} onChange={e => setForm({...form, name:e.target.value})} placeholder="Branch Name" />
          <Input value={form.address} onChange={e => setForm({...form, address:e.target.value})} placeholder="Address" />
          <PhoneInput value={form.phone} onChange={(v) => setForm({...form, phone:v})} placeholder="Branch phone" />
          <div className="flex gap-2">
            <Button onClick={handleCreate} className="rounded-full">Save</Button>
            <Button variant="outline" onClick={() => setShowAdd(false)} className="rounded-full">Cancel</Button>
          </div>
        </div>
      )}
      {branches.map(b => (
        <div key={b.id} className="border rounded-xl px-4 py-3 mb-2">
          <p className="text-sm font-medium">{b.name}</p>
          <p className="text-xs text-muted-foreground">{b.address||'No address'} &middot; {b.phone||'No phone'}</p>
        </div>
      ))}
    </div>
  );
}

// ── Callbacks ──
function CallbacksTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const limit = 15;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    if (debouncedSearch) params.append('q', debouncedSearch);
    api.get(`/admin/callback-requests?${params.toString()}`).then(r => {
      const d = r.data.data || {};
      setItems(d.callback_requests || []);
      setTotal(d.total || 0);
    }).catch(()=>{});
  }, [page, debouncedSearch]);
  useEffect(() => { load(); }, [load]);

  const markDone = async (id) => {
    try {
      await api.put(`/admin/callback-requests/${id}/status`, {status: 'CLOSED'});
      toast.success('Marked done');
      setItems(prev => prev.map(i => i.id === id ? {...i, status: 'CLOSED'} : i));
    } catch { toast.error('Failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this callback request? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/callback-requests/${id}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed'); }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div data-testid="callbacks-tab">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          placeholder="Search by name or phone..."
          className="max-w-sm"
          data-testid="callbacks-search"
        />
        <span className="text-xs text-muted-foreground ml-auto" data-testid="callbacks-total">
          {total} {total === 1 ? 'result' : 'results'}
        </span>
      </div>
      {items.length === 0 && <p className="text-sm text-muted-foreground">No callbacks.</p>}
      {items.map(i => (
        <div key={i.id} className="border rounded-xl px-4 py-3 mb-2 flex items-center justify-between" data-testid={`callback-${i.id}`}>
          <div>
            <p className="text-sm font-medium">{i.name}</p>
            <p className="text-xs text-muted-foreground">{i.mobile} &middot; {new Date(i.created_at).toLocaleString()}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${i.status === 'CLOSED' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{i.status || 'NEW'}</span>
            {i.status !== 'CLOSED' && <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => markDone(i.id)} data-testid={`callback-done-${i.id}`}>Mark Done</Button>}
            <Button size="sm" variant="outline" className="text-xs h-7 text-destructive" onClick={() => remove(i.id)} data-testid={`callback-delete-${i.id}`}>Delete</Button>
          </div>
        </div>
      ))}
      {total > 0 && (
        <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-border/50">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="text-xs h-7" data-testid="callbacks-prev-btn">Prev</Button>
          <span className="text-xs text-muted-foreground tabular-nums" data-testid="callbacks-pagination">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="text-xs h-7" data-testid="callbacks-next-btn">Next</Button>
        </div>
      )}
    </div>
  );
}

// ── Consultations ──
function ConsultationsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const limit = 15;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    if (debouncedSearch) params.append('q', debouncedSearch);
    api.get(`/admin/consultation-requests?${params.toString()}`).then(r => {
      const d = r.data.data || {};
      setItems(d.consultation_requests || []);
      setTotal(d.total || 0);
    }).catch(()=>{});
  }, [page, debouncedSearch]);
  useEffect(() => { load(); }, [load]);

  const markDone = async (id) => {
    try {
      await api.put(`/admin/consultation-requests/${id}/status`, {status: 'COMPLETED'});
      toast.success('Marked done');
      setItems(prev => prev.map(i => i.id === id ? {...i, status: 'COMPLETED'} : i));
    } catch { toast.error('Failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this consultation request? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/consultation-requests/${id}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed'); }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div data-testid="consultations-tab">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          placeholder="Search by name or phone..."
          className="max-w-sm"
          data-testid="consultations-search"
        />
        <span className="text-xs text-muted-foreground ml-auto" data-testid="consultations-total">
          {total} {total === 1 ? 'result' : 'results'}
        </span>
      </div>
      {items.length === 0 && <p className="text-sm text-muted-foreground">No consultations.</p>}
      {items.map(i => (
        <div key={i.id} className="border rounded-xl px-4 py-3 mb-2 flex items-center justify-between" data-testid={`consultation-${i.id}`}>
          <div>
            <p className="text-sm font-medium">{i.name}</p>
            <p className="text-xs text-muted-foreground">{i.mobile} &middot; {i.preferred_date} &middot; {new Date(i.created_at).toLocaleString()}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${i.status === 'COMPLETED' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{i.status || 'NEW'}</span>
            {i.status !== 'COMPLETED' && <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => markDone(i.id)} data-testid={`consultation-done-${i.id}`}>Mark Done</Button>}
            <Button size="sm" variant="outline" className="text-xs h-7 text-destructive" onClick={() => remove(i.id)} data-testid={`consultation-delete-${i.id}`}>Delete</Button>
          </div>
        </div>
      ))}
      {total > 0 && (
        <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-border/50">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="text-xs h-7" data-testid="consultations-prev-btn">Prev</Button>
          <span className="text-xs text-muted-foreground tabular-nums" data-testid="consultations-pagination">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="text-xs h-7" data-testid="consultations-next-btn">Next</Button>
        </div>
      )}
    </div>
  );
}

// ── Pincodes ──
function PincodesTab() {
  const { user } = useAuth();
  const [pincodes, setPincodes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [sel, setSel] = useState('');
  const [newPin, setNewPin] = useState('');
  const [newBranch, setNewBranch] = useState('');
  const isSA = user?.role === 'SUPER_ADMIN';

  useEffect(() => { api.get('/admin/branches').then(r => setBranches(r.data.data||[])).catch(()=>{}); }, []);
  const load = useCallback(() => {
    const p = sel ? `?branch_id=${sel}` : '';
    api.get(`/admin/pincodes${p}`).then(r => {
      const raw = r.data.data || [];
      // For display, keep one entry per (pincode, branch_id) — already deduped at backend
      setPincodes(raw);
    }).catch(()=>{});
  }, [sel]);
  useEffect(() => { load(); }, [load]);

  const branchName = (bid) => {
    if (!bid) return 'Unassigned';
    const b = branches.find(x => x.id === bid || x.ID === bid);
    return b ? b.name : 'Unknown';
  };

  const handleAdd = async () => {
    if (!newPin) { toast.error('Pincode required'); return; }
    if (isSA && !newBranch) { toast.error('Select a branch — pincodes must be associated with a store'); return; }
    try {
      await api.post('/admin/pincodes', { pincode: newPin, branch_id: isSA ? newBranch : undefined });
      toast.success('Pincode added');
      setNewPin('');
      load();
    } catch(e) { toast.error(e.response?.data?.error || 'Failed'); }
  };
  const handleRemove = async (code, branchId) => {
    try {
      // If user is in "all branches" view, use the branch-aware delete query param
      await api.delete(`/admin/pincodes/${code}${branchId ? `?branch_id=${branchId}` : ''}`);
      toast.success('Removed');
      load();
    } catch { toast.error('Failed'); }
  };

  // Group pincodes by branch for "All" view
  const groupedByBranch = pincodes.reduce((acc, p) => {
    const code = typeof p === 'string' ? p : p.pincode;
    const bid = typeof p === 'string' ? '' : (p.branch_id || '');
    if (!acc[bid]) acc[bid] = [];
    acc[bid].push(code);
    return acc;
  }, {});

  return (
    <div data-testid="pincodes-tab">
      {isSA && branches.length > 0 && (
        <div className="mb-4">
          <Select value={sel || '__ALL__'} onValueChange={(v) => setSel(v === '__ALL__' ? '' : v)}>
            <SelectTrigger className="w-[200px] h-9 text-sm" data-testid="pincode-filter-branch">
              <SelectValue placeholder="All Branches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__ALL__" data-testid="pincode-filter-branch-all">All Branches</SelectItem>
              {branches.map(b => (
                <SelectItem key={b.id} value={b.id} data-testid={`pincode-filter-branch-${b.id}`}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <Input
          value={newPin}
          onChange={e => setNewPin(e.target.value.replace(/\D/g,'').slice(0,6))}
          placeholder="Add pincode..."
          className="max-w-[140px]"
          maxLength={6}
          data-testid="add-pincode-input"
        />
        {isSA && (
          <Select value={newBranch} onValueChange={setNewBranch}>
            <SelectTrigger className="w-[200px] h-9 text-sm" data-testid="add-pincode-branch">
              <SelectValue placeholder="Select branch *" />
            </SelectTrigger>
            <SelectContent>
              {branches.map(b => (
                <SelectItem key={b.id} value={b.id} data-testid={`add-pincode-branch-${b.id}`}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button onClick={handleAdd} className="rounded-full" data-testid="add-pincode-btn">Add</Button>
      </div>
      <p className="text-xs text-muted-foreground mb-2">{pincodes.length} pincode-branch mappings</p>
      {sel ? (
        // Single branch view — flat list
        <div className="flex flex-wrap gap-2">
          {pincodes.map((p, i) => {
            const code = typeof p === 'string' ? p : p.pincode;
            const bid = typeof p === 'string' ? '' : p.branch_id;
            return (
              <div key={`${code}-${bid}-${i}`} className="flex items-center gap-1 bg-muted rounded-full px-3 py-1 text-xs" data-testid={`pincode-${code}`}>
                <span>{code}</span>
                <button onClick={() => handleRemove(code, bid)} className="text-muted-foreground hover:text-destructive"><X className="w-3 h-3" /></button>
              </div>
            );
          })}
        </div>
      ) : (
        // All branches view — grouped
        <div className="space-y-3">
          {Object.entries(groupedByBranch).map(([bid, codes]) => (
            <div key={bid || 'unassigned'} className="border rounded-xl p-3">
              <p className="text-xs font-semibold mb-2">{branchName(bid)} <span className="text-muted-foreground font-normal">({codes.length})</span></p>
              <div className="flex flex-wrap gap-2">
                {codes.map((code, i) => (
                  <div key={`${code}-${i}`} className="flex items-center gap-1 bg-muted rounded-full px-3 py-1 text-xs" data-testid={`pincode-${code}-${bid}`}>
                    <span>{code}</span>
                    <button onClick={() => handleRemove(code, bid)} className="text-muted-foreground hover:text-destructive"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Audit ──
function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const limit = 30;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    if (debouncedSearch) params.append('order_id', debouncedSearch);
    api.get(`/admin/audit-logs?${params.toString()}`).then(r => {
      const d = r.data.data || {};
      setLogs(d.logs || []);
      setTotal(d.total || 0);
    }).catch(() => {});
  }, [page, debouncedSearch]);
  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div data-testid="audit-tab">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          placeholder="Filter by Order ID (e.g. ORD-1783244104-1717)..."
          className="max-w-sm"
          data-testid="audit-order-filter"
        />
        <span className="text-xs text-muted-foreground ml-auto" data-testid="audit-total">
          {total} {total === 1 ? 'entry' : 'entries'} · <span className="italic">auto-purged after 30 days</span>
        </span>
      </div>
      {logs.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No audit logs match.</p>}
      {logs.map(l => (
        <div key={l.id} className="border-b py-2 text-xs" data-testid={`audit-log-${l.id}`}>
          <span className="text-muted-foreground">{new Date(l.created_at).toLocaleString()}</span>
          <span className="mx-2 font-medium">{l.user_name}</span>
          <span className="text-primary">{l.action}</span> on <span className="font-medium">{l.entity}</span>
          {l.details && <span className="text-muted-foreground ml-2">— {l.details}</span>}
        </div>
      ))}
      {total > 0 && (
        <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-border/50">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="text-xs h-7" data-testid="audit-prev-btn">Prev</Button>
          <span className="text-xs text-muted-foreground tabular-nums" data-testid="audit-pagination">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="text-xs h-7" data-testid="audit-next-btn">Next</Button>
        </div>
      )}
    </div>
  );
}
